/**
 * Proof of Grind – hero slider.
 * Scroll-snap track in de lijst; pijltjes, dots, autoplay en bijbehorende captions.
 * Geen afhankelijkheid van Horizon-componenten, zodat hij los werkt.
 */
class PogHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.slides = Array.from(this.querySelectorAll('.pog-hero__slide'));
    this.captions = Array.from(this.querySelectorAll('.pog-hero__caption'));
    this.dots = Array.from(this.querySelectorAll('.pog-hero__dot'));
    this.prev = this.querySelector('[ref="prev"]');
    this.next = this.querySelector('[ref="next"]');
    this.index = 0;
    this.timer = null;
    this.scrollTimer = null;

    if (!this.track || this.slides.length === 0) return;
    this.setActive(0);

    this.prev?.addEventListener('click', () => this.go(this.index - 1, true));
    this.next?.addEventListener('click', () => this.go(this.index + 1, true));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index), true)));

    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncFromScroll(), 80);
    }, { passive: true });

    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, true); }
    });

    const pause = () => this.stop();
    const resume = () => this.start();
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.track.addEventListener('touchstart', pause, { passive: true });
    this.track.addEventListener('touchend', () => setTimeout(resume, 1500), { passive: true });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));

    // Alleen autoplay als de slider in beeld is
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
      }, { threshold: 0.4 });
      this.io.observe(this);
    } else {
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    this.io?.disconnect();
  }

  get autoplay() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return this.dataset.autoplay === 'true' && this.slides.length > 1;
  }

  start() {
    if (!this.autoplay || this.timer) return;
    const interval = Math.max(2, Number(this.dataset.interval) || 5) * 1000;
    this.timer = setInterval(() => this.go(this.index + 1, false), interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  syncFromScroll() {
    const w = this.track.clientWidth || 1;
    const i = Math.round(this.track.scrollLeft / w);
    if (i !== this.index) this.setActive(i);
  }

  go(i, userInitiated) {
    const n = this.slides.length;
    const target = ((i % n) + n) % n;
    const w = this.track.clientWidth;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.track.scrollTo({ left: target * w, behavior });
    this.setActive(target);
    if (userInitiated) {
      this.stop();
      setTimeout(() => this.start(), 4000);
    }
  }

  setActive(i) {
    this.index = i;
    const frame = this.querySelector('.pog-hero__frame');
    const slide = this.slides[i];
    if (frame && slide) frame.dataset.noFrame = String(slide.classList.contains('pog-hero__slide--no-frame'));
    this.captions.forEach((c, idx) => (c.hidden = idx !== i));
    this.dots.forEach((d, idx) => d.setAttribute('aria-selected', String(idx === i)));
  }
}

if (!customElements.get('pog-hero-slider')) {
  customElements.define('pog-hero-slider', PogHeroSlider);
}
